import React, { useCallback } from "react";
import { Flex, Typography } from "antd";
import { message as antMessage } from "antd/lib";
import { chatApi, uchatApi } from "../../../../services/api";
import { RatingButtons } from "../../../ChatUtils";
import type { ChatMessage } from "../types";
import {
  transformMessages,
  insertTimestamps,
  shouldShowTimestamp,
  createV2MessageTimeFooter,
} from "./messageProcessor";

/** listMemory 旁路 typing 字段 */
export interface ChatTypingPayload {
  active?: boolean;
  source?: string;
  actor?: string;
  updatedAt?: number;
}

interface UseMessageHandlersProps {
  csUuidRef: React.MutableRefObject<string | undefined>;
  setMessages: React.Dispatch<React.SetStateAction<any[]>>;
  setChatStatus: (status: string) => void;
  messageRatings: Record<string, number>;
  setMessageRatings: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  handleEvaluationSubmit: (rating: number, feedback?: string) => Promise<void>;
  handleEvaluationSkip: () => void;
  styles: any;
  intl: any;
  themeColors?: any;
  uiVariant?: "legacy" | "v2";
  /** 用户发消息成功后：开始等待回复（仅加速轮询，不立刻展示 typing） */
  onAwaitReplyStart?: (opts?: { minReplyAfterSec?: number }) => void;
  /** 开始等待回复的时间（秒），用于判断是否已有新回复 */
  awaitingStartedAtRef?: React.MutableRefObject<number>;
  /** 轮询结果：同步服务端 typing；hasNewAgentReply 仅在本轮已 setMessages 后为 true */
  onTypingSync?: (payload: {
    serverTyping: boolean;
    hasPendingReply: boolean;
    hasNewAgentReply: boolean;
  }) => void;
}

/**
 * 功能描述：从 memory 列表推导是否仍在等待客服回复
 * 业务逻辑：空 BOT 占位或 USER answering 表示 AI 生成中
 */
const detectPendingReply = (memories: any[]): boolean => {
  if (!Array.isArray(memories)) return false;
  return memories.some((item) => {
    if (!item) return false;
    // AI 空占位：BOT + 无 content + waitAnswer
    if (
      item.type === "BOT" &&
      !item.content &&
      item.status === "waitAnswer"
    ) {
      return true;
    }
    // USER 正在回答中
    if (item.type === "USER" && item.status === "answering") {
      return true;
    }
    return false;
  });
};

/**
 * 功能描述：是否出现带内容的 BOT/HUMAN 新回复
 */
const detectAgentReplyWithContent = (
  memories: any[],
  sinceSec: number
): boolean => {
  if (!Array.isArray(memories) || sinceSec <= 0) return false;
  return memories.some((item) => {
    if (!item || !item.content) return false;
    if (item.type !== "BOT" && item.type !== "HUMAN") return false;
    const ts = Number(item.modifyTime || item.createTime || 0);
    return ts >= sinceSec - 2;
  });
};

export const useMessageHandlers = ({
  csUuidRef,
  setMessages,
  setChatStatus,
  messageRatings,
  setMessageRatings,
  handleEvaluationSubmit,
  handleEvaluationSkip,
  styles,
  intl,
  themeColors,
  uiVariant,
  onAwaitReplyStart,
  awaitingStartedAtRef,
  onTypingSync,
}: UseMessageHandlersProps) => {
  // 创建消息 footer 的辅助函数（仅显示评分按钮，不显示时间）
  const createMessageFooter = useCallback(
    (options?: {
      uuid?: string;
      rating?: number;
      onRate?: (uuid: string, rating: number) => Promise<void>;
      timestamp?: number;
    }) => {
      const { uuid, rating, onRate, timestamp } = options || {};

      if (uiVariant === "v2" && timestamp) {
        return createV2MessageTimeFooter(timestamp);
      }

      const showRating = uuid && rating !== undefined && onRate;

      if (!showRating) {
        return null;
      }

      return (
        <Flex
          justify="flex-end"
          align="center"
          className={styles.fullWidthRow}
          style={{
            width: "100%",
            position: "relative",
          }}
        >
          <RatingButtons uuid={uuid} rating={rating} onRate={onRate} />
        </Flex>
      );
    },
    [styles.fullWidthRow, uiVariant]
  );

  // 创建时间戳内容（用于独立的时间戳消息）
  const createTimestampContent = useCallback((timestamp: number) => {
    return (
      <Flex justify="center" align="center" style={{ width: "100%" }}>
        <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
          {new Date(timestamp * 1000).toLocaleString("zh-CN", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          })}
        </Typography.Text>
      </Flex>
    );
  }, []);

  // 评分处理函数（需要在fetchChatContent之前定义）
  const handleRateMessage = useCallback(
    async (uuid: string, rating: number) => {
      try {
        const currentRating = messageRatings[uuid];

        // 如果点击的是当前已选中的评分，则取消评分
        if (currentRating === rating) {
          const response = await chatApi.rateMemory({
            uuid: uuid,
            rating: 0, // 0表示取消评分
          });

          if (response.success) {
            // 更新评分状态为未评分
            setMessageRatings((prev) => {
              const newRatings = { ...prev };
              delete newRatings[uuid];
              return newRatings;
            });
          }
          return;
        }

        // 如果点击的是不同的评分，则切换评分
        const response = await chatApi.rateMemory({
          uuid: uuid,
          rating: rating,
        });

        if (response.success) {
          // 更新评分状态
          setMessageRatings((prev) => ({
            ...prev,
            [uuid]: rating,
          }));
        }
      } catch (error) {
        console.error("Error rating message:", error);
      }
    },
    [messageRatings, setMessageRatings]
  );

  // 创建QA记录
  const createQaRecord = useCallback(
    async (
      message: string | null,
      imageUrl: string | null,
      videoUrl: string | null,
      fileUrl: string | null
    ) => {
      try {
        const chatQA = {
          csUuid: csUuidRef.current,
          content: message || "",
          imageUrl: imageUrl,
          videoUrl: videoUrl,
          fileUrl: fileUrl,
        };

        const createResponse = await chatApi.createMemory(chatQA);

        // 检查是否返回C_1错误码（聊天已关闭）
        if (
          createResponse &&
          createResponse.success === false &&
          createResponse.code === "C_1"
        ) {
          setChatStatus("closed-no-eval");
          return null;
        }

        // 创建成功后，添加消息到聊天记录
        if (createResponse.success && createResponse.data) {
          const list = createResponse.data;
          setMessages((prevMessages) => {
            const newMessages = [...prevMessages];

            // 获取上一条有时间戳的消息，用于判断是否需要显示时间
            let previousTimestamp: number | undefined;
            for (let i = newMessages.length - 1; i >= 0; i--) {
              if (newMessages[i].timestamp) {
                previousTimestamp = newMessages[i].timestamp;
                break;
              }
            }

            // 如果有图片，添加图片消息
            if (imageUrl) {
              const imageMemory = list.find(
                (item: any) => item.contentType === "IMAGE"
              );
              const imageTimestamp = imageMemory?.modifyTime;
              const shouldShow =
                uiVariant !== "v2" &&
                shouldShowTimestamp(imageTimestamp, previousTimestamp);

              // 如果需要显示时间戳，先插入时间戳消息
              if (shouldShow) {
                newMessages.push({
                  key: `timestamp-${imageTimestamp}-${Date.now()}`,
                  role: "system",
                  content: createTimestampContent(imageTimestamp),
                  messageType: "text",
                });
              }

              newMessages.push({
                key: `${imageMemory.uuid}`,
                role: "local",
                content: imageUrl,
                timestamp: imageTimestamp,
                footer:
                  uiVariant === "v2" && imageTimestamp
                    ? createMessageFooter({ timestamp: imageTimestamp })
                    : undefined,
              });

              // 更新 previousTimestamp 为当前消息的时间戳
              previousTimestamp = imageTimestamp;
            }

            // 如果有文字，添加文字消息
            if (message) {
              const textMemory = list.find(
                (item: any) => item.contentType === "TEXT"
              );
              const textTimestamp = textMemory?.modifyTime;
              const shouldShow =
                uiVariant !== "v2" &&
                shouldShowTimestamp(textTimestamp, previousTimestamp);

              // 如果需要显示时间戳，先插入时间戳消息
              if (shouldShow) {
                newMessages.push({
                  key: `timestamp-${textTimestamp}-${Date.now()}`,
                  role: "system",
                  content: createTimestampContent(textTimestamp),
                  messageType: "text",
                });
              }

              newMessages.push({
                key: `${textMemory.uuid}`,
                role: "local",
                content: message,
                timestamp: textTimestamp,
                footer:
                  uiVariant === "v2" && textTimestamp
                    ? createMessageFooter({ timestamp: textTimestamp })
                    : undefined,
              });

              // 更新 previousTimestamp 为当前消息的时间戳
              previousTimestamp = textTimestamp;
            }

            // 如果有视频，添加视频消息
            if (videoUrl) {
              const videoMemory = list.find(
                (item: any) => item.contentType === "VIDEO"
              );
              const videoTimestamp = videoMemory?.modifyTime;
              const shouldShow =
                uiVariant !== "v2" &&
                shouldShowTimestamp(videoTimestamp, previousTimestamp);

              // 如果需要显示时间戳，先插入时间戳消息
              if (shouldShow) {
                newMessages.push({
                  key: `timestamp-${videoTimestamp}-${Date.now()}`,
                  role: "system",
                  content: createTimestampContent(videoTimestamp),
                  messageType: "text",
                });
              }

              newMessages.push({
                key: `${videoMemory.uuid}`,
                role: "local",
                content: videoUrl,
                timestamp: videoTimestamp,
                footer:
                  uiVariant === "v2" && videoTimestamp
                    ? createMessageFooter({ timestamp: videoTimestamp })
                    : undefined,
              });

              // 更新 previousTimestamp 为当前消息的时间戳
              previousTimestamp = videoTimestamp;
            }

            // 如果有文件，添加文件消息
            if (fileUrl) {
              const fileMemory = list.find(
                (item: any) => item.contentType === "DOCUMENT"
              );
              const fileTimestamp = fileMemory?.modifyTime;
              const shouldShow =
                uiVariant !== "v2" &&
                shouldShowTimestamp(fileTimestamp, previousTimestamp);

              // 如果需要显示时间戳，先插入时间戳消息
              if (shouldShow) {
                newMessages.push({
                  key: `timestamp-${fileTimestamp}-${Date.now()}`,
                  role: "system",
                  content: createTimestampContent(fileTimestamp),
                  messageType: "text",
                });
              }

              newMessages.push({
                key: `${fileMemory.uuid}`,
                role: "local",
                content: fileUrl,
                timestamp: fileTimestamp,
                footer:
                  uiVariant === "v2" && fileTimestamp
                    ? createMessageFooter({ timestamp: fileTimestamp })
                    : undefined,
              });
            }

            return newMessages;
          });

          // 发消息成功：进入等待回复（加速轮询），不立刻展示 typing
          onAwaitReplyStart?.();
          return createResponse.data;
        }
        antMessage.error("Failed to create conversation!");
        return null;
      } catch (error) {
        console.error("Error creating QA record:", error);
        antMessage.error("Failed to create conversation!");
        return null;
      }
    },
    [csUuidRef, setMessages, setChatStatus, createTimestampContent, createMessageFooter, uiVariant, onAwaitReplyStart]
  );

  // 获取聊天内容
  const fetchChatContent = useCallback(async () => {
    if (!csUuidRef.current) return;
    const combinedMessages: ChatMessage[] = [];

    try {
      const response = await chatApi.listMemory({
        csUuid: csUuidRef.current,
        pageSize: 30,
        current: 1,
      });

      // 检查是否返回C_1错误码（聊天已关闭）
      if (response && response.success === false && response.code === "C_1") {
        setChatStatus("close");
        return;
      }

      const memories = response?.data || [];
      const typing = response?.typing as ChatTypingPayload | undefined;
      let serverTyping = Boolean(typing?.active);
      const hasPendingReply = detectPendingReply(memories);
      const sinceSec = awaitingStartedAtRef?.current || 0;
      const hasNewAgentReply = detectAgentReplyWithContent(memories, sinceSec);
      let messagesApplied = false;

      if (response.success && memories.length > 0) {
        // 使用消息处理器转换消息
        const localMessages = transformMessages(memories, {
          messageRatings,
          handleRateMessage,
          handleEvaluationSubmit,
          handleEvaluationSkip,
          intl,
          themeColors,
          uiVariant,
          createMessageFooter,
          createTimestampContent,
        });

        combinedMessages.push(...localMessages);

        // 插入时间戳消息
        const processedMessages = insertTimestamps(
          combinedMessages,
          createTimestampContent,
          uiVariant
        );

        // 先写入消息列表，再同步 typing，保证「有气泡后才关提示」同帧生效
        setMessages(processedMessages);
        messagesApplied = true;
      }

      try {
        const csRes = await uchatApi.queryCs(csUuidRef.current);
        if (csRes?.success && csRes.data?.status === "close") {
          setChatStatus("close");
        }
        // fetch 旁路 typing（与 list 互补）
        if (csRes?.success && csRes.data?.typingActive && !serverTyping) {
          serverTyping = true;
        }
      } catch {
        // 忽略会话状态查询失败，不影响拉消息
      }

      // 仅当本轮已把带内容的回复写入 UI 时，才允许关闭 sticky 提示
      onTypingSync?.({
        serverTyping,
        hasPendingReply,
        hasNewAgentReply: messagesApplied && hasNewAgentReply,
      });
    } catch (error) {
      console.error("Error fetching chat content:", error);
    }
  }, [
    csUuidRef,
    messageRatings,
    handleRateMessage,
    intl,
    handleEvaluationSubmit,
    handleEvaluationSkip,
    setMessages,
    createMessageFooter,
    createTimestampContent,
    themeColors,
    uiVariant,
    setChatStatus,
    onTypingSync,
    awaitingStartedAtRef,
  ]);

  return {
    createQaRecord,
    fetchChatContent,
    handleRateMessage,
  };
};
